import { useNavigate } from 'react-router';
import { ABRIDGED_RETIRED_MESSAGE, outputProfileOfTypeName } from '@/core/outputProfile';
import { ReportTypeCode, type ReportRow } from '@/core/models/reports.models';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import { reportsService } from '@/core/services/reports';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyState } from '@/kit/EmptyState';
import { Icon } from '@/kit/Icon';
import { Menu, useMenu } from '@/kit/Menu';
import { SelectionBar } from '@/kit/SelectionBar';
import { Tooltip } from '@/kit/Tooltip';
import {
  REPORT_NOT_PRODUCIBLE,
  isSubmissionFor,
  submissionDescription,
  validationChecksRun,
} from './accountsFiling';
import { ReportMenuItem, CannotBeProduced, ReportSource } from './ReportListParts';
import { SendTrialBalance } from './SendTrialBalance';
import { TaxreturnReportsettingsOption } from './TaxreturnReportsettingsOption';
import { ClientKind, useClientKind } from '@/features/tr-reportsettings/clientKind';
import { useAccountsReportList } from './useAccountsReportList';
import './AccountsReportList.scss';

/** The accounting period behind this report's trial balance, if any - the link real Companies House filing needs. */
const accountingPeriodId = async (trialBalanceId: string): Promise<string | null> =>
  (await reportsService.getTrialBalanceReport(trialBalanceId)).accountingPeriodId;

/**
 * This report's submission: its still-open one, or else its most recent (to show how it went), or a freshly created
 * one. A new one is named after the report, as Submit Accounts names it, so deleting the report removes it too (#169).
 * #168/#171: another report's submission for the same period - open or already sent - is never taken for this one.
 */
async function submissionIdForReport(clientId: string, periodId: string, row: ReportRow): Promise<string> {
  const submissions = await filingService.getSubmissions(clientId);
  const mine = submissions.filter((s) => s.accountingPeriodId === periodId && isSubmissionFor(s, row.refNo));
  const existing = mine.find((s) => s.canSubmit) ?? mine[0];
  return existing
    ? existing.id
    : (await filingService.createSubmission(clientId, periodId, submissionDescription(row))).id;
}

/** Annual Accounts list (Angular TaxreturnReportsComponent), `/home/taxreturns/reports-accounts`. */
export function TaxreturnReports() {
  const navigate = useNavigate();

  const list = useAccountsReportList({
    reportType: ReportTypeCode.Annual,
    reportTitle: 'Annual Accounts',
    // #169: a deleted report's unsent submission is removed with it. Submissions started from Submit Accounts carry the
    // report's reference in their description; ones already sent to Companies House are kept (the API refuses them).
    onReportsDeleted: (client, rows) => {
      if (rows.length === 0) {
        return;
      }
      filingService.getSubmissions(client.id).then(
        (submissions) =>
          submissions
            .filter((s) => s.canDelete && rows.some((r) => isSubmissionFor(s, r.refNo)))
            .forEach((s) => void filingService.deleteSubmission(s.id).catch(() => undefined)),
        () => undefined,
      );
    },
  });
  const { client, load } = list;
  const kind = useClientKind(client?.id);
  const isSoleTrader =
    kind?.clientType === ClientKind.SoleTrader || Boolean(client?.name?.toLowerCase().includes('soletrader'));
  const isPartnership = kind?.clientType === ClientKind.Partnership;
  const pageTitle = isSoleTrader
    ? 'Sole Trader Accounts (Financial Statement)'
    : isPartnership
      ? 'Partnership Accounts (Financial Statement)'
      : null;

  const openReportSettings = () => list.openReportOption(TaxreturnReportsettingsOption);
  const emailReport = (row: ReportRow) => list.email(row, SendTrialBalance);

  /**
   * BUG-049: downloads this report's own inline XBRL. The API tags the figures the report prints (the same build as
   * its PDF), so the file always matches the report - it no longer depends on a separately generated accounts
   * document for the period, which a report never creates.
   */
  const downloadIxbrl = async (row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    try {
      await reportsService.downloadReportIxbrl(row.id);
    } catch (error) {
      notifyBlobError(error, 'The iXBRL file could not be produced.');
    }
  };

  const sendToCapisign = async (row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    try {
      await reportsService.sendToCapisign(row.id);
    } catch (error) {
      // Capisign is not connected for any firm today, so this is the expected outcome; the message comes from the API.
      notifyError(error);
      return;
    }
    notifySuccess(`${row.refNo} sent to Capisign for signature.`);
    void load();
  };

  /** Resets a Capisign status that does not reflect a real send (e.g. set while testing the badge). */
  const clearCapisignStatus = async (row: ReportRow) => {
    try {
      await reportsService.clearCapisignStatus(row.id);
    } catch (error) {
      notifyError(error);
      return;
    }
    notifySuccess(`${row.refNo}'s Capisign status has been reset to Not sent.`);
    void load();
  };

  /**
   * Opens the real Companies House submission wizard for this report, already resolved to (or newly created for)
   * this report's accounting period. D05 (as Capium 3.0): the client's size does not restrict which report is submitted.
   */
  const submitToCompaniesHouse = async (row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    // APT341: an Abridged report made before is still listed and opens, but it can no longer be filed.
    if (outputProfileOfTypeName(row.type) === 'ABRIDGED') {
      notifyError(ABRIDGED_RETIRED_MESSAGE);
      return;
    }
    if (!client) {
      return;
    }
    const clientId = client.id;
    let outcome: { periodId: string | null; submissionId: string | null };
    try {
      const periodId = await accountingPeriodId(row.currentTrialBalanceId);
      const submissionId = periodId ? await submissionIdForReport(clientId, periodId, row) : null;
      outcome = { periodId, submissionId };
    } catch (error) {
      notifyError(error);
      return;
    }
    const { periodId, submissionId } = outcome;
    if (submissionId !== null) {
      navigate(`/home/taxreturns/submitlist?id=${encodeURIComponent(submissionId)}`);
    }
    if (!periodId) {
      notifyError(
        "This report's trial balance is not linked to an accounting period, so it cannot be submitted to Companies House.",
      );
    }
  };

  const changeStatus = async (row: ReportRow) => {
    try {
      const updated = await reportsService.changeReportStatus(row.id);
      notifySuccess(
        updated.submitStatusId === 1
          ? `${updated.refNo} marked as submitted.`
          : `${updated.refNo} marked as not submitted.`,
      );
    } catch (error) {
      notifyError(error);
      return;
    }
    void load();
  };

  /**
   * Checks the report against the Companies House filing rules that don't need iXBRL and refreshes the row so the grid
   * shows Valid/Has issues straight away.
   */
  const validateReport = async (row: ReportRow) => {
    if (row.canProduce === false) {
      notifyError(row.blockedReason ?? REPORT_NOT_PRODUCIBLE);
      return;
    }
    try {
      const result = await reportsService.validateReport(row.id);
      // #165: say what was checked, not just "valid".
      const warnings = result.checks.filter((c) => !c.isBlocking).map((c) => c.message);
      if (result.isValid) {
        notifySuccess(
          `${row.refNo} passed the pre-filing checks: ${validationChecksRun(row)}. ` +
            (warnings.length ? `Warnings: ${warnings.join(' ')} ` : '') +
            'Companies House validates the accounts themselves when they are submitted.',
        );
      } else {
        notifyError(result.checks.map((c) => c.message).join(' '));
      }
      void load();
    } catch (error) {
      notifyError(error);
    }
  };

  const columns: DataTableColumn<ReportRow>[] = [
    {
      id: 'refno',
      header: (
        <>
          <Checkbox
            checked={list.isAllSelected()}
            indeterminate={list.isPartlySelected()}
            onChange={(event) => list.toggleAll(event.target.checked)}
          />
          Ref. No.
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={list.selection.has(element.id)}
            onChange={(event) => list.toggleRow(element, event.target.checked)}
          />
          <a className="link" onClick={() => list.previewReport(element)}>
            {element.refNo}
          </a>
        </>
      ),
    },
    {
      id: 'fromdate',
      header: 'From Date',
      cell: (element) => <span className="white-space-nowrap">{element.fromDate}</span>,
    },
    {
      id: 'todate',
      header: 'To Date',
      cell: (element) => <span className="white-space-nowrap">{element.toDate}</span>,
    },
    { id: 'type', header: 'Type', cell: (element) => (isSoleTrader ? 'Sole Trader Accounts' : element.type) },
    { id: 'description', header: 'Description', cell: (element) => element.description },
    {
      id: 'balance',
      header: 'Trial Balance(s)',
      cell: (element) => (
        <>
          <a className="link" onClick={() => list.onTrialBalanceClick(element)}>
            {element.trialBalances}
          </a>
          {/* prod-p3: say so in the list rather than letting the user find out from a failed download. */}
          <CannotBeProduced row={element} />
        </>
      ),
    },
    // APT342: Source trial balance or Adjusted trial balance, so a report's figures can be traced.
    { id: 'source', header: 'Source', cell: (element) => <ReportSource row={element} /> },
    { id: 'updated', header: 'Last Updated', cell: (element) => element.updated },
    { id: 'submitstatus', header: 'Submit Status', cell: (element) => element.submitStatus },
    ...(!isSoleTrader && !isPartnership
      ? [
          {
            id: 'chvalidation',
            header: 'CH Validation',
            cell: (element: ReportRow) => (
              // task-165: Validate said what it checked only in a toast, which is gone in seconds, leaving a bare
              // "Valid" under a "CH Validation" heading that reads as Companies House's own verdict. Keep the checks
              // on the value itself, and say plainly that Companies House validates only on submission.
              <Tooltip
                title={
                  element.chValidationStatusId === 1
                    ? `Passed the pre-filing checks: ${validationChecksRun(element)}. ` +
                      'Companies House validates the accounts itself only when they are submitted.'
                    : null
                }
              >
                <span
                  className={
                    element.chValidationStatusId === 2
                      ? 'text-red'
                      : element.chValidationStatusId === 1
                        ? 'text-green'
                        : undefined
                  }
                >
                  {element.chValidationStatus}
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
    { id: 'capisignstatus', header: 'Capisign Status', cell: (element) => element.capisignStatus },
    {
      // task-167: the row actions shared the Capisign Status cell, so that heading ran on over the
      // download and delete icons as if it labelled them, while the status was a lone "-" under its
      // first letter. The actions get their own column, and the heading sits over the status alone.
      // BUG-050: headed "Action" as in 3.0, and kept in view at any width (AccountsReportList.scss).
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Download" onClick={() => void list.download(element)} disabled={element.canProduce === false}>
            <Icon name="file_download" outlined />
          </Button>
          <Button title="Email" onClick={() => void emailReport(element)} disabled={element.canProduce === false}>
            <Icon name="mail" outlined />
          </Button>
          <Button
            title="Regenerate"
            onClick={() => void list.regenerate(element)}
            disabled={element.canProduce === false}
          >
            <Icon name="sync" outlined />
          </Button>
          <Button title="Delete" onClick={() => void list.openDeleteClick(element)}>
            <Icon name="delete_outline" outlined />
          </Button>
          <RowMenu
            row={element}
            isSoleTrader={isSoleTrader}
            actions={{
              edit: () => list.onEditReport(element),
              downloadIxbrl: () => void downloadIxbrl(element),
              validate: () => void validateReport(element),
              sendToCapisign: () => void sendToCapisign(element),
              clearCapisignStatus: () => void clearCapisignStatus(element),
              submit: () => void submitToCompaniesHouse(element),
              changeStatus: () => void changeStatus(element),
              viewSource: () => list.viewSource(element),
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-accounts-list tr-accounts-list-annual">
      <div className="content-body">
        {pageTitle && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="report-page-title">{pageTitle}</h2>
          </div>
        )}
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={list.onAddnewReport}>
              {!isSoleTrader && <Icon name="add" />}
              <span>{isSoleTrader ? '+ Add Report' : 'Add New Report'}</span>
            </Button>
            {/* #157: no client picker here - this screen is already inside the open client. */}
          </div>

          <div className="content-header-right mb-1">
            <Button
              className={`action-button secondary-button ${isSoleTrader ? 'btn-report-settings-soletrader' : ''}`}
              onClick={openReportSettings}
            >
              <Icon name="edit_note" outlined />
              Report Settings
            </Button>
          </div>
        </div>
        <SelectionBar
          count={list.selection.size}
          onDelete={() => void list.deleteSelected()}
          onClear={list.clearSelection}
        >
          <Button className="action-button secondary-button" onClick={() => void list.downloadSelected()}>
            <Icon name="file_download" outlined />
            <span>Download selected</span>
          </Button>
        </SelectionBar>
        <div className="mat-table-wrapper">
          <DataTable
            stickyHeader
            columns={columns}
            rows={list.dataSource}
            rowKey={(row) => row.id}
            noDataMessage={
              list.isLoaded ? (
                <EmptyState
                  icon="event_note"
                  title="No reports yet"
                  hint="Create the first report for this client from its trial balance."
                  action={
                    <Button className="action-button primary-button" onClick={list.onAddnewReport}>
                      <Icon name="add" />
                      <span>Create the first report</span>
                    </Button>
                  }
                />
              ) : undefined
            }
          />
        </div>
      </div>
    </SideMenuPage>
  );
}

interface RowActions {
  edit: () => void;
  downloadIxbrl: () => void;
  validate: () => void;
  sendToCapisign: () => void;
  clearCapisignStatus: () => void;
  submit: () => void;
  changeStatus: () => void;
  viewSource: () => void;
}

/** The kebab menu of an Annual Accounts row. */
function RowMenu({ row, actions, isSoleTrader }: { row: ReportRow; actions: RowActions; isSoleTrader?: boolean }) {
  const menu = useMenu();
  return (
    <>
      <Button className="more-button" aria-label="More actions" onClick={menu.onOpen}>
        <Icon name="more_vert" />
      </Button>
      <Menu {...menu} xPosition="before" className="more-button-menu scroll">
        <ReportMenuItem icon="edit" title="Edit" onClick={actions.edit}>
          <span>Edit</span>
        </ReportMenuItem>
        {!isSoleTrader && (
          <ReportMenuItem icon="file_download" title="Download as iXBRL" onClick={actions.downloadIxbrl}>
            <span>Download as iXBRL</span>
          </ReportMenuItem>
        )}
        {!isSoleTrader && (
          <ReportMenuItem icon="fact_check" title="Validate" onClick={actions.validate}>
            <span>Validate</span>
          </ReportMenuItem>
        )}
        <ReportMenuItem icon="send" title="Send to Capisign" onClick={actions.sendToCapisign}>
          <span>Send to Capisign</span>
        </ReportMenuItem>
        {row.capisignStatusId !== 0 && (
          <ReportMenuItem
            icon="restart_alt"
            title="Reset a Capisign status that wasn't set by a real send"
            onClick={actions.clearCapisignStatus}
          >
            <span>Clear Capisign Status</span>
          </ReportMenuItem>
        )}
        {!isSoleTrader && (
          <ReportMenuItem icon="content_paste" title="Submit" onClick={actions.submit}>
            <span>Submit to Companies House</span>
          </ReportMenuItem>
        )}
        <ReportMenuItem icon="swap_horizontal_circle" title="Change Status" onClick={actions.changeStatus}>
          <span>Change Status</span>
        </ReportMenuItem>
        {/* APT342: where the figures came from, the journals in them, and every generation. */}
        <ReportMenuItem icon="account_tree" title="Source and lineage" onClick={actions.viewSource}>
          <span>Source and lineage</span>
        </ReportMenuItem>
      </Menu>
    </>
  );
}
